import React from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

const LicenseList = ({ listItem = [], setList, hitAxios }) => {
  const [selectedLicense, setSelectedLicense] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState("");

  // Parse each license once and handle malformed JSON safely.
  const rows = React.useMemo(
    () =>
      [...listItem].reverse().map((license) => {
        let metadata = {};

        try {
          metadata =
            typeof license.json === "string"
              ? JSON.parse(license.json)
              : license.json || {};
        } catch {
          metadata = {};
        }

        return {
          ...license,
          logo: metadata?.item?.previews?.icon_preview?.icon_url || "",
          itemId: metadata?.item?.id || "",
          productName: metadata?.item?.name || "",
          buyer: metadata?.buyer || "",
        };
      }),
    [listItem],
  );

  function openDeleteDialog(license) {
    setError("");
    setSelectedLicense(license);
  }

  function closeDeleteDialog() {
    if (deleting) return;

    setSelectedLicense(null);
    setError("");
  }

  async function deleteLicense() {
    if (!selectedLicense || deleting) return;

    const id = selectedLicense.id;

    setDeleting(true);
    setError("");

    try {
      const res = await hitAxios({
        path: "/api/admin/delete_custom_license",
        post: true,
        admin: true,
        token_user: localStorage.getItem(
          process.env.REACT_APP_TOKEN + "_hamid",
        ),
        obj: { id },
      });

      if (!res?.data?.success) {
        setError(res?.data?.msg || "Unable to delete the license");
        return;
      }

      setList((previous) => previous.filter((license) => license.id !== id));

      setSelectedLicense(null);
    } catch (err) {
      setError(err?.response?.data?.msg || "Unable to delete the license");
    } finally {
      setDeleting(false);
    }
  }

  const columns = [
    {
      headerName: "Logo",
      field: "logo",
      flex: 0.15,
      minWidth: 70,
      sortable: false,
      renderCell: (params) =>
        params.value ? (
          <CardMedia
            component="img"
            src={params.value}
            alt={params.row.productName || "Product logo"}
            sx={{
              height: 40,
              width: 40,
              borderRadius: 2,
              objectFit: "contain",
            }}
          />
        ) : null,
    },
    {
      headerName: "Id",
      field: "itemId",
      flex: 0.2,
      minWidth: 90,
    },
    {
      headerName: "Purchase code",
      field: "purchase_code",
      flex: 0.6,
      minWidth: 260,
    },
    {
      headerName: "Product",
      field: "productName",
      flex: 1,
      minWidth: 180,
    },
    {
      headerName: "Buyer",
      field: "buyer",
      flex: 0.3,
      minWidth: 120,
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          color="error"
          variant="outlined"
          size="small"
          disabled={deleting}
          onClick={(event) => {
            event.stopPropagation();
            openDeleteDialog(params.row);
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <>
      <Box bgcolor="action.hover" height="80vh">
        <DataGrid
          getRowId={(row) => row.id}
          rows={rows}
          columns={columns}
          pageSize={50}
          rowsPerPageOptions={[50]}
          checkboxSelection={false}
          sx={{
            boxShadow: 0,
            borderRadius: 4,
            border: 0,
            fontSize: 13,
            padding: 2,
          }}
        />
      </Box>

      <Dialog
        open={Boolean(selectedLicense)}
        onClose={closeDeleteDialog}
        disableEscapeKeyDown={deleting}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete license?</DialogTitle>

        <DialogContent>
          <DialogContentText>
            This will permanently delete the custom license
            {selectedLicense?.productName
              ? ` for "${selectedLicense.productName}"`
              : ""}
            . This action cannot be undone.
          </DialogContentText>

          <Box
            sx={{
              mt: 2,
              fontSize: 13,
              overflowWrap: "anywhere",
            }}
          >
            <strong>Purchase code:</strong> {selectedLicense?.purchase_code}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>
            Cancel
          </Button>

          <Button
            onClick={deleteLicense}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete license"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LicenseList;
